/**
 * CharcutnuvoGeo — Hybrid GeoIP state detection module
 * Determines the visitor's US state for retail-first routing.
 *
 * Detection priority:
 *   1. localStorage cache (7-day TTL)
 *   2. Server-side state code passed from Liquid
 *   3. Client-side fetch from ipapi.co (3-second timeout)
 *
 * Exposes:
 *   window.CharcutnuvoGeo.init(opts)
 *   window.CharcutnuvoGeo.isRetailState()
 *   window.CharcutnuvoGeo.state  — resolved two-letter state code or null
 *
 * Dispatches:
 *   cn:geo:ready — CustomEvent on document with { state, isRetail } detail
 */
(function () {
  'use strict';

  var CACHE_KEY = 'cn_user_state';
  var CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

  var CharcutnuvoGeo = {

    /** @type {string|null} Resolved two-letter US state code, or null */
    state: null,

    /** @type {string[]} Uppercase state codes that qualify as retail states */
    retailStates: [],

    /**
     * Initialize the geo detection module.
     *
     * @param {Object} opts
     * @param {string} opts.serverState    — State code from Liquid server-side detection (may be empty)
     * @param {string} opts.retailStatesCSV — Comma-separated state codes from theme settings
     */
    init: function (opts) {
      opts = opts || {};

      // Parse retailStatesCSV: split by comma, trim, uppercase, filter blanks
      this.retailStates = (opts.retailStatesCSV || '')
        .split(',')
        .map(function (s) { return s.trim().toUpperCase(); })
        .filter(function (s) { return s.length > 0; });

      var serverState = (opts.serverState || '').trim().toUpperCase();

      // Detection priority: cache -> serverState -> fetch
      var cached = this._getCache();

      if (cached) {
        this.state = cached;
        this._applyRouting();
        return;
      }

      if (serverState) {
        this.state = serverState;
        this._setCache(serverState);
        this._applyRouting();
        return;
      }

      // No cache, no server state — fetch from ipapi.co
      var self = this;
      this._fetchState().then(function (fetchedState) {
        if (fetchedState) {
          self.state = fetchedState;
          self._setCache(fetchedState);
        }
        self._applyRouting();
      });
    },

    /**
     * Returns true if the user's detected state is in the retailStates list.
     *
     * @returns {boolean}
     */
    isRetailState: function () {
      if (!this.state) return false;
      return this.retailStates.indexOf(this.state.toUpperCase()) !== -1;
    },

    /**
     * Apply routing classes and data attributes to the DOM, then
     * dispatch the cn:geo:ready event.
     */
    _applyRouting: function () {
      var isRetail = this.isRetailState();

      // Set data attributes on body
      document.body.dataset.userState = this.state || '';
      document.body.dataset.isRetailState = isRetail ? 'true' : 'false';

      // Toggle CSS classes on elements with [data-cn-routing]
      var routingEls = document.querySelectorAll('[data-cn-routing]');
      for (var i = 0; i < routingEls.length; i++) {
        var el = routingEls[i];
        if (isRetail) {
          el.classList.add('cn-retail-user');
          el.classList.remove('cn-dtc-user');
        } else {
          el.classList.add('cn-dtc-user');
          el.classList.remove('cn-retail-user');
        }
      }

      // Dispatch custom event
      var event = new CustomEvent('cn:geo:ready', {
        detail: { state: this.state, isRetail: isRetail }
      });
      document.dispatchEvent(event);
    },

    /**
     * Fetch the user's state from ipapi.co with a 3-second timeout.
     * Only resolves to a state code if country_code is 'US'.
     *
     * @returns {Promise<string|null>} Two-letter state code or null
     */
    _fetchState: function () {
      return new Promise(function (resolve) {
        var controller;
        var signal;
        var timeoutId;

        // Set up AbortController with 3-second timeout if supported
        if (typeof AbortController !== 'undefined') {
          controller = new AbortController();
          signal = controller.signal;
          timeoutId = setTimeout(function () {
            controller.abort();
          }, 3000);
        }

        var fetchOpts = {};
        if (signal) fetchOpts.signal = signal;

        fetch('https://ipapi.co/json/', fetchOpts)
          .then(function (response) {
            if (!response.ok) {
              resolve(null);
              return;
            }
            return response.json();
          })
          .then(function (data) {
            if (!data) {
              resolve(null);
              return;
            }
            if (data.country_code === 'US' && data.region_code) {
              resolve(data.region_code.toUpperCase());
            } else {
              resolve(null);
            }
          })
          .catch(function () {
            // Network error, timeout, or AbortError — resolve null silently
            resolve(null);
          })
          .finally(function () {
            if (timeoutId) clearTimeout(timeoutId);
          });
      });
    },

    /**
     * Read the cached state from localStorage.
     * Returns the state code string if valid and within TTL, otherwise null.
     * Fails silently on any error (private browsing, quota, etc.).
     *
     * @returns {string|null}
     */
    _getCache: function () {
      try {
        var raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return null;

        var data = JSON.parse(raw);
        if (!data || !data.state || !data.ts) return null;

        var age = Date.now() - data.ts;
        if (age > CACHE_TTL) {
          localStorage.removeItem(CACHE_KEY);
          return null;
        }

        return data.state;
      } catch (e) {
        return null;
      }
    },

    /**
     * Write the state code to localStorage with a timestamp.
     * Fails silently on any error.
     *
     * @param {string} state — Two-letter state code to cache
     */
    _setCache: function (state) {
      try {
        var data = JSON.stringify({ state: state, ts: Date.now() });
        localStorage.setItem(CACHE_KEY, data);
      } catch (e) {
        // Fail silently — private browsing, quota exceeded, etc.
      }
    }
  };

  window.CharcutnuvoGeo = CharcutnuvoGeo;
})();
